import api from '@/api'

const state = {
  alerts: [],
  activeAlerts: [],
  alertHistory: [],
  loading: false,
  error: null
}

const mutations = {
  SET_ALERTS(state, alerts) {
    state.alerts = alerts
  },
  SET_ACTIVE_ALERTS(state, alerts) {
    state.activeAlerts = alerts
  },
  SET_ALERT_HISTORY(state, alerts) {
    state.alertHistory = alerts
  },
  ADD_ALERT(state, alert) {
    state.alerts.unshift(alert)
    if (alert.status === 'active') {
      state.activeAlerts.unshift(alert)
    }
  },
  UPDATE_ALERT(state, { id, updates }) {
    const alertIndex = state.alerts.findIndex(alert => alert.id === id)
    if (alertIndex !== -1) {
      state.alerts[alertIndex] = { ...state.alerts[alertIndex], ...updates }
    }
    
    const activeIndex = state.activeAlerts.findIndex(alert => alert.id === id)
    if (activeIndex !== -1) {
      state.activeAlerts[activeIndex] = { ...state.activeAlerts[activeIndex], ...updates }
    }
  },
  SET_LOADING(state, loading) {
    state.loading = loading
  },
  SET_ERROR(state, error) {
    state.error = error
  },
  CLEAR_ERROR(state) {
    state.error = null
  }
}

const actions = {
  async fetchAlerts({ commit }) {
    try {
      commit('SET_LOADING', true)
      commit('CLEAR_ERROR')
      
      const response = await api.get('/alerts')
      commit('SET_ALERTS', response.data)
    } catch (error) {
      commit('SET_ERROR', 'Error al cargar alertas')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async fetchActiveAlerts({ commit }) {
    try {
      const response = await api.get('/alerts/active')
      commit('SET_ACTIVE_ALERTS', response.data)
    } catch (error) {
      commit('SET_ERROR', 'Error al cargar alertas activas')
      throw error
    }
  },

  async fetchAlertHistory({ commit }, params = {}) {
    try {
      const response = await api.get('/alerts/history', { params })
      commit('SET_ALERT_HISTORY', response.data)
    } catch (error) {
      commit('SET_ERROR', 'Error al cargar historial de alertas')
      throw error
    }
  },

  async acknowledgeAlert({ commit }, id) {
    try {
      await api.post(`/alerts/${id}/acknowledge`)
      commit('UPDATE_ALERT', { id, updates: { acknowledged: true, acknowledgedAt: new Date().toISOString() } })
      return { success: true }
    } catch (error) {
      const message = error.response && error.response.data && error.response.data.message || 'Error al reconocer alerta'
      commit('SET_ERROR', message)
      throw error
    }
  },

  async createAlert({ commit, dispatch }, alertData) {
    try {
      commit('SET_LOADING', true)
      commit('CLEAR_ERROR')
      
      const response = await api.post('/alerts', alertData)
      commit('ADD_ALERT', response.data)
      
      return { success: true, alert: response.data }
    } catch (error) {
      const message = error.response && error.response.data && error.response.data.message || 'Error al crear alerta'
      commit('SET_ERROR', message)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async updateAlert({ commit, dispatch }, { id, alertData }) {
    try {
      commit('SET_LOADING', true)
      commit('CLEAR_ERROR')
      
      await api.put(`/alerts/${id}`, alertData)
      await dispatch('fetchAlerts')
      
      return { success: true }
    } catch (error) {
      const message = error.response && error.response.data && error.response.data.message || 'Error al actualizar alerta'
      commit('SET_ERROR', message)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async deleteAlert({ commit, dispatch }, id) {
    try {
      commit('SET_LOADING', true)
      commit('CLEAR_ERROR')
      
      await api.delete(`/alerts/${id}`)
      await dispatch('fetchAlerts')
      
      return { success: true }
    } catch (error) {
      const message = error.response && error.response.data && error.response.data.message || 'Error al eliminar alerta'
      commit('SET_ERROR', message)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async initialize({ dispatch }) {
    try {
      await Promise.all([
        dispatch('fetchAlerts'),
        dispatch('fetchActiveAlerts')
      ])
    } catch (error) {
      // Error initializing alerts module - handled silently
    }
  }
}

const getters = {
  alerts: state => state.alerts,
  activeAlerts: state => state.activeAlerts,
  alertHistory: state => state.alertHistory,
  loading: state => state.loading,
  error: state => state.error,
  
  unacknowledgedAlerts: state => state.alerts.filter(alert => !alert.acknowledged),
  criticalAlerts: state => state.alerts.filter(alert => alert.severity === 'critical'),
  warningAlerts: state => state.alerts.filter(alert => alert.severity === 'warning'),
  
  getAlertById: state => id => {
    return state.alerts.find(alert => alert.id === id)
  },
  
  getAlertsByDevice: state => deviceId => {
    return state.alerts.filter(alert => alert.deviceId === deviceId)
  },
  
  getAlertsBySensor: state => sensorId => {
    return state.alerts.filter(alert => alert.sensorId === sensorId)
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
} 


// WEBPACK FOOTER //
// ./src/store/modules/alerts.js